'use strict';

import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {Observer} from 'gsap/Observer';
import wrapWords from '../../../src/js/shared/wrapWords';

const $ = jQuery.noConflict();
$(function() {

	// Color values mirror $green / $green-pale in src/scss/config/_variables.scss.
	// If those variables change, update these constants too.
	const COLOR_FILLED = '#0f4722';
	const COLOR_PALE = '#0f47221a';

	// While the section is pinned, wheel/trackpad/touch input is intercepted and
	// fed into a speed-limited scroll of our own. The reveal stays tied to scroll
	// position — nothing plays on its own — but the section can't be crossed
	// faster than MAX_STEP allows, so the text always has time to fill in.
	//
	// MAX_STEP is px per 60fps frame (scaled for other refresh rates below), so
	// 8 ≈ 480px/s. This is the hard ceiling — spinning the wheel faster cannot
	// move the section any quicker, it only fills the queue. MAX_QUEUE caps how
	// far ahead input may bank: a hard flick glides on for about that distance
	// instead of jumping the whole section. ENTRY_MOMENTUM (seconds of incoming
	// velocity) carries the gesture that entered the section into that glide, so
	// taking over doesn't feel like a wall.
	const MAX_STEP = 8;
	const MAX_QUEUE = 450;
	const ENTRY_MOMENTUM = 0.12;

	const logoReveal = {

		init: function() {
			this.cacheDOM();
			if (!this.section || !this.steps.length) {
				return;
			}
			this.wrapRevealWords();
			this.updateNextSectionOffset();
			window.addEventListener('resize', () => this.updateNextSectionOffset());
			if (this.prefersReducedMotion()) {
				return;
			}
			gsap.registerPlugin(ScrollTrigger, Observer);
			ScrollTrigger.config({ignoreMobileResize: true});
			this.createTimeline();
			// refreshInit, not refresh: the negative margin changes document height,
			// so it has to land before ScrollTrigger takes its measurements.
			ScrollTrigger.addEventListener('refreshInit', () => this.updateNextSectionOffset());
		},

		cacheDOM: function() {
			this.section = document.querySelector('.js-logo-reveal');
			if (!this.section) {
				this.steps = [];
				return;
			}
			this.pin = this.section.querySelector('.js-logo-reveal-pin');
			this.logo = this.section.querySelector('.js-logo-reveal-logo');
			this.shapes = this.section.querySelectorAll('.js-logo-reveal-shape');
			this.steps = this.section.querySelectorAll('.js-logo-reveal-step');
			this.inner = this.section.querySelector('.js-logo-reveal-inner');
			this.block = this.section.closest('.fws-block');
			this.nextSection = this.block ? this.block.nextElementSibling : this.section.nextElementSibling;
		},

		updateNextSectionOffset: function() {
			if (!this.inner || !this.nextSection) {
				return;
			}
			const offset = (window.innerHeight - this.inner.offsetHeight) / 2;
			this.nextSection.style.marginTop = offset > 0 ? `-${offset}px` : '';
		},

		prefersReducedMotion: function() {
			return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		},

		wrapRevealWords: function() {
			const subtitles = this.section.querySelectorAll('.js-logo-reveal-subtitle');
			wrapWords.wrap(subtitles, 'logo-reveal__reveal-word', 'js-logo-reveal-word');
		},

		createTimeline: function() {
			const mm = gsap.matchMedia();
			const stepCount = this.steps.length;
			const shapeCount = this.shapes.length;
			// Total segments = one per step + a trailing one to fill any remaining
			// shapes (e.g. the dot/head on top of the logo) after the last text.
			const totalSegments = stepCount + Math.max(0, shapeCount - stepCount);

			mm.add({
				isDesktop: '(min-width: 768px)',
				isMobile: '(max-width: 767px)',
			}, (ctx) => {
				const {isDesktop} = ctx.conditions;
				// contentPercent paces the steps/shapes exactly as before. tailPadRatio
				// then adds idle scroll on top, held after the last step has fully
				// faded out, so the next section doesn't arrive until the user
				// scrolls a bit further past it.
				const contentPercent = isDesktop ? 330 : 190;
				const tailPadRatio = 0.1;
				const pinDistance = `+=${Math.round(contentPercent * (1 + tailPadRatio))}%`;

				gsap.set(this.shapes, {color: COLOR_PALE});
				gsap.set(this.steps, {opacity: 0});
				gsap.set(this.section.querySelectorAll('.js-logo-reveal-word'), {opacity: 0.1});

				let armed = false;
				let target = 0;
				let observer = null;

				// Function declarations, not arrow consts: ScrollTrigger can fire its
				// toggle callbacks while it is still being created (when the section
				// is already in view on load), which is before the observer exists.
				function tick() {
					const current = window.scrollY;
					const diff = target - current;
					if (!diff) {
						return;
					}
					// deltaRatio keeps the speed limit in px/second rather than
					// px/frame, so 120Hz displays don't scroll twice as fast.
					const max = MAX_STEP * gsap.ticker.deltaRatio(60);
					window.scrollTo(0, current + Math.max(-max, Math.min(max, diff)));
				}

				function arm() {
					if (!observer || armed) {
						return;
					}
					armed = true;
					// Seed the queue with the momentum of the gesture that brought the
					// user here, so handing over to the limiter glides instead of
					// stopping dead at the section edge.
					const carry = tl.scrollTrigger.getVelocity() * ENTRY_MOMENTUM;
					target = window.scrollY + Math.max(-MAX_QUEUE, Math.min(MAX_QUEUE, carry));
					observer.enable();
					gsap.ticker.add(tick);
				}

				function disarm() {
					if (!observer || !armed) {
						return;
					}
					armed = false;
					observer.disable();
					gsap.ticker.remove(tick);
				}

				const tl = gsap.timeline({
					defaults: {ease: 'power1.inOut'},
					scrollTrigger: {
						trigger: this.section,
						start: 'top top',
						end: pinDistance,
						pin: this.section,
						pinSpacing: true,
						// 1:1: the reveal follows the scroll position exactly, and the
						// smoothing comes from the limiter instead. Scrub lag on top of
						// that reads as the animation lurching on after you stop.
						scrub: true,
						invalidateOnRefresh: true,
						onEnter: () => arm(),
						onEnterBack: () => arm(),
						onLeave: () => disarm(),
						onLeaveBack: () => disarm(),
					},
				});

				const segDur = 1 / totalSegments;

				for (let i = 0; i < stepCount; i++) {
					const segStart = i * segDur;

					if (i > 0) {
						tl.to(this.steps[i - 1], {
							opacity: 0,
							duration: segDur * 0.2,
						}, segStart);
					}

					if (this.shapes[i]) {
						tl.to(this.shapes[i], {
							color: COLOR_FILLED,
							duration: segDur * 0.35,
						}, segStart);
					}

					tl.fromTo(this.steps[i],
						{opacity: 0, y: 24},
						{opacity: 1, y: 0, duration: segDur * 0.3},
						segStart + segDur * 0.2,
					);

					const words = this.steps[i].querySelectorAll('.js-logo-reveal-word');
					if (words.length) {
						// stagger.amount distributes across a fixed window so the reveal
						// always finishes inside the segment regardless of word count.
						tl.to(words, {
							opacity: 1,
							ease: 'none',
							stagger: {amount: segDur * 0.55, from: 'start'},
							duration: segDur * 0.1,
						}, segStart + segDur * 0.25);
					}
				}

				// Trailing segment(s): fill any logo shapes that don't have a
				// matching text step (typically the dot on top of the logo).
				for (let i = stepCount; i < shapeCount; i++) {
					const segStart = i * segDur;

					if (i === stepCount && stepCount > 0) {
						tl.to(this.steps[stepCount - 1], {
							opacity: 0,
							duration: segDur * 0.2,
						}, segStart);
					}

					tl.to(this.shapes[i], {
						color: COLOR_FILLED,
						duration: segDur * 0.6,
					}, segStart + segDur * 0.3);
				}

				// Idle hold: adds no animation, just consumes scroll distance so
				// there's a gap after the last fade before the section unpins.
				tl.to({}, {duration: tl.duration() * tailPadRatio});

				observer = Observer.create({
					type: 'wheel,touch',
					// Aligns wheel with touch: both then report a negative deltaY when
					// the user is moving further down the page.
					wheelSpeed: -1,
					preventDefault: true,
					// Observer batches its events into one call per frame, so deltaY
					// here is the whole gesture for this frame.
					onChangeY: (self) => {
						if (!armed) {
							return;
						}
						const current = window.scrollY;
						const next = target - self.deltaY;
						// Clamp against the live scroll position, not the old target,
						// so momentum can never bank more than MAX_QUEUE ahead.
						target = Math.max(current - MAX_QUEUE, Math.min(current + MAX_QUEUE, next));
					},
				});

				observer.disable();
				if (tl.scrollTrigger.isActive) {
					arm();
				}

				return () => {
					disarm();
					observer.kill();
					gsap.set([...this.shapes, ...this.steps], {clearProps: 'all'});
				};
			});
		},

	};

	logoReveal.init();
});
